import React, { useState } from 'react';
import api from '../services/api';
import { Loader2, Check } from 'lucide-react';

function BulkCategorizeModal({ isOpen, onClose, onComplete, goalIds, workspaceCategories }) {
    const [selectedCategoryIds, setSelectedCategoryIds] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    if (!isOpen || !goalIds || goalIds.length === 0) return null;

    const handleToggleCategory = (catId) => {
        setSelectedCategoryIds(prev => 
            prev.includes(catId) 
                ? prev.filter(id => id !== catId) 
                : [...prev, catId]
        );
    };

    const handleSave = async () => {
        if (selectedCategoryIds.length === 0) {
            setError('Please select at least one category');
            return;
        }

        setSubmitting(true);
        setError('');
        try {
            await api.post('/tests/goals/bulk-categorize', {
                goalIds,
                categoryIds: selectedCategoryIds
            });
            if (onComplete) {
                onComplete();
            }
            onClose();
        } catch (err) {
            console.error('Bulk categorize error:', err);
            setError(err.response?.data?.error || 'Failed to bulk categorize test goals');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="dialog-overlay" onClick={onClose}>
            <div className="dialog-container" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '440px' }}>
                <div className="dialog-header">
                    <h3 className="dialog-title">Categorize Goals</h3>
                    <button className="dialog-close" onClick={onClose}>
                        ×
                    </button>
                </div>

                <div className="dialog-body" style={{ padding: '20px' }}>
                    {error && (
                        <div className="error-alert" style={{
                            padding: '10px 14px',
                            backgroundColor: '#ef444420',
                            border: '1px solid #ef444460',
                            borderRadius: '6px',
                            color: '#ef4444',
                            fontSize: '13px',
                            marginBottom: '16px'
                        }}>
                            {error}
                        </div>
                    )}

                    <p style={{ color: 'var(--text-secondary)', fontSize: '14px', marginBottom: '20px' }}>
                        Select categories to add to the <strong>{goalIds.length}</strong> selected test goal{goalIds.length !== 1 ? 's' : ''}:
                    </p>

                    <div className="categories-checklist" style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '10px',
                        maxHeight: '240px',
                        overflowY: 'auto',
                        padding: '4px',
                        marginBottom: '20px'
                    }}>
                        {workspaceCategories.length === 0 ? (
                            <div style={{ color: 'var(--text-muted)', fontSize: '13px', textAlign: 'center', padding: '10px 0' }}>
                                No categories defined in this workspace. Create them on the New Goal page or in Workspace Settings first.
                            </div>
                        ) : (
                            workspaceCategories.map(cat => {
                                const isChecked = selectedCategoryIds.includes(cat.id);
                                return (
                                    <div
                                        key={cat.id}
                                        onClick={() => handleToggleCategory(cat.id)}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            padding: '10px 14px',
                                            borderRadius: '8px',
                                            backgroundColor: isChecked ? 'rgba(56, 189, 248, 0.08)' : 'var(--bg-tertiary, #151c2c)',
                                            border: `1px solid ${isChecked ? 'var(--primary, #38bdf8)' : 'var(--border, #2e374a)'}`,
                                            cursor: 'pointer',
                                            transition: 'all 0.15s ease'
                                        }}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <span style={{
                                                display: 'inline-block',
                                                width: '10px',
                                                height: '10px',
                                                borderRadius: '50%',
                                                backgroundColor: cat.color || '#94a3b8'
                                            }} />
                                            <span style={{ fontSize: '13px', color: 'var(--text-primary)', fontWeight: 500 }}>
                                                {cat.name}
                                            </span>
                                        </div>
                                        {isChecked && <Check size={16} style={{ color: 'var(--primary, #38bdf8)' }} />}
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>

                <div className="dialog-footer">
                    <button
                        className="btn btn-ghost btn-sm"
                        onClick={onClose}
                        disabled={submitting}
                    >
                        Cancel
                    </button>
                    <button
                        className="btn btn-primary btn-sm"
                        onClick={handleSave}
                        disabled={submitting || workspaceCategories.length === 0}
                        style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                    >
                        {submitting && <Loader2 size={14} className="animate-spin" />}
                        Apply Categories
                    </button>
                </div>
            </div>
        </div>
    );
}

export default BulkCategorizeModal;
